import React from 'react';
import {
    CreditCard,
    TrendingUp,
    TrendingDown,
    DollarSign,
    Activity,
    Package,
    Eye,
    BarChart3,
    ArrowRight,
    ChevronRight
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

const UnifiedMetrics = ({ performanceData, paymentData, catalog }) => {
    const navigate = useNavigate();

    const hasPayments = catalog?.allowOnlinePayment &&
        catalog?.paymentSettings?.bankAccount?.verificationStatus === 'VERIFIED';

    // Calculate payment metrics
    const totalEarnings = paymentData?.totalEarnings || 0;
    const totalTransactions = paymentData?.totalTransactions || 0;
    const earningsChange = paymentData?.earningsPercentageChange || 0;
    const transactionsChange = paymentData?.transactionsPercentageChange || 0;

    // Performance metrics
    const performanceMetrics = [
        {
            title: 'Items',
            value: performanceData.totalItems || 0,
            icon: Package,
            link: '/catalog',
            color: 'text-blue-600',
            bg: 'bg-gray-100',
            border: ''
        },
        {
            title: 'Categories',
            value: performanceData.totalCategories || 0,
            icon: BarChart3,
            link: '/catalog',
            color: 'text-purple-600',
            bg: 'bg-gray-100',
            border: ''
        },
        {
            title: 'Visitors',
            value: performanceData.weeklyCatalogViews || 0,
            icon: Eye,
            link: '/statistics',
            color: 'text-green-600',
            bg: 'bg-gray-100',
            border: ''
        }
    ];

    // Helper function to render trend indicator
    const renderTrendIndicator = (percentage) => {
        if (percentage === 0) return null;

        const isPositive = percentage > 0;
        const TrendIcon = isPositive ? TrendingUp : TrendingDown;
        const colorClass = isPositive ? 'text-green-600' : 'text-red-600';
        const bgClass = isPositive ? 'bg-green-50' : 'bg-red-50';

        return (
            <div className={`flex items-center justify-center gap-1 ${colorClass} ${bgClass} px-2 py-1 rounded-full`}>
                <TrendIcon className="w-3 h-3" />
                <span className="text-xs lg:text-base font-medium">
                    {Math.abs(percentage).toFixed(1)}%
                </span>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-lg shadow-lg ">
            {/* Header */}
            <div className="p-6 pb-3">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-gray-600" />
                        <h3 className="text-xl font-bold text-gray-900">Store Performance</h3>
                    </div>
                    <div className="bg-blue-50 px-2 py-1 rounded text-sm lg:text-base font-medium text-blue-700">
                        7 days
                    </div>
                </div>

                {/* Performance Metrics Grid */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                    {performanceMetrics.map((metric, index) => {
                        const IconComponent = metric.icon;
                        return (
                            <Link
                                key={index}
                                to={metric.link}
                                className={`${metric.bg} ${metric.border} border rounded-lg p-3 hover:shadow-sm transition-all duration-200`}
                            >
                                <div className="text-center">
                                    <IconComponent className={`w-5 h-5 ${metric.color} mx-auto mb-2`} />
                                    <p className={`text-lg font-bold ${metric.color}`}>
                                        {metric.value}
                                    </p>
                                    <p className="text-xs lg:text-base text-gray-600">{metric.title}</p>
                                </div>
                            </Link>
                        );
                    })}
                </div>

                {/* Performance Change Indicator */}
                {performanceData.performancePercentageChange !== undefined && (
                    <div className="flex items-center justify-center mb-4">
                        <div className="bg-gray-50 rounded-lg px-3 py-2 flex items-center gap-2">
                            <span className="text-sm lg:text-base text-gray-600">Weekly Performance:</span>
                            {renderTrendIndicator(performanceData.performancePercentageChange)}
                        </div>
                    </div>
                )}
            </div>

            {/* Payment Section */}
            <div className="border-t border-gray-100">
                {!hasPayments ? (
                    <div className="p-6">
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 text-center">
                            <div className="bg-white rounded-full p-2 w-10 h-10 mx-auto mb-2 flex items-center justify-center shadow-sm">
                                <CreditCard className="w-5 h-5 text-blue-600" />
                            </div>
                            <h4 className="text-sm lg:text-base font-medium text-gray-900 mb-1">Enable Payments</h4>
                            <p className="text-xs lg:text-base text-gray-600 mb-3">Start accepting payments directly</p>
                            <button
                                onClick={() => navigate('/payment-settings')}
                                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium text-sm lg:text-base flex items-center gap-2 mx-auto"
                            >
                                Set Up Payments
                                <ArrowRight className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="p-6">
                        <div className="flex items-center gap-2 mb-3">
                            <CreditCard className="w-4 h-4 text-gray-600" />
                            <h4 className="text-sm lg:text-base font-medium text-gray-900">Payment Overview</h4>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-3 border border-green-200">
                                <div className="flex items-center justify-between mb-2">
                                    <DollarSign className="w-4 h-4 text-green-600" />
                                    {renderTrendIndicator(earningsChange)}
                                </div>
                                <p className="text-lg font-bold text-green-700">
                                    ₦{totalEarnings.toLocaleString()}
                                </p>
                                <p className="text-xs lg:text-base text-green-700 font-medium">Total Earnings</p>
                            </div>

                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                                <div className="flex items-center justify-between mb-2">
                                    <Activity className="w-4 h-4 text-blue-600" />
                                    {renderTrendIndicator(transactionsChange)}
                                </div>
                                <p className="text-lg font-bold text-blue-700">
                                    {totalTransactions}
                                </p>
                                <p className="text-xs lg:text-base text-blue-700 font-medium">Total Sales</p>
                            </div>
                        </div>

                        {(totalEarnings > 0 || totalTransactions > 0) && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                                <button
                                    onClick={() => navigate('/transactions')}
                                    className="w-full text-center bg-gray-50 text-blue-600 hover:text-blue-700 font-medium text-sm lg:text-base"
                                >
                                    View All Transactions →
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UnifiedMetrics;